import React from "react";
import Input from "../../../../components/FormComponents/Input";
import Label from "../../../../components/FormComponents/Label";
import SingleSelectBox from "../../../../components/FormComponents/SingleSelectBox";
import MultiSelectBox from "../../../../components/FormComponents/MultiSelectBox";
import { getLabelForKey } from "../../../../utils/Config/DefaultConfig/formFieldConfig";
import { ocrFormats } from "../../../../utils/Config/DefaultConfig/fileFormats";
import ButtonActionIcon from "../../../../components/BuilderComponents/ButtonActionIcon";
import Button from "../../../../components/FormComponents/Button";
import AiModelSelector from "../../../../components/BuilderComponents/AiModelSelector/AiModelSelector";
import ErrorMessage from "../../../../components/FormComponents/ErrorMessage";

const DOCUMENT_TYPES = ["aadhar", "pan", "bank_passbook", "gst", "cheque"];

const OUTPUT_LABELS_BY_TYPE = {
  aadhar: {
    uuid: "Aadhaar Number",
    vid: "Virtual ID",
    name: "Full Name",
    careOf: "Care Of",
    dob: "Date of Birth",
    gender: "Gender",
    address: "Address",
    phone_number: "Phone Number"
  },
  pan: {
    pan: "PAN Number",
    birthDate: "Date of Birth",
    incorporationDate: "Incorporation Date",
    name: "Full Name",
    father: "Father’s Name",
    category: "Category"
  },
  bank_passbook: {
    customerId: "Customer ID",
    name: "Account Holder Name",
    accountNumber: "Account Number",
    address: "Account Holder Address",
    branch: "Branch Name",
    ifsc: "IFSC Code",
    micr: "MICR Code",
    branchAddress: "Branch Address",
    opened: "Opening Date"
  },
  gst: {
    gstin: "GSTIN",
    legalName: "Legal Name",
    tradeName: "Trade Name",
    constitution: "Constitution",
    address: "Registered Address",
    type: "Registration Type",
    registered: "Registration Date",
    expiry: "Expiry Date",
    issued: "Issued Date",
    approvingAuthority: "Approving Authority",
    jurisdiction: "Jurisdiction",
    provisional: "Provisional Status",
    pan: "PAN",
    tin: "TIN",
    slr: "SLR"
  },
  cheque: {
    signatory: "Signatory",
    accountNumber: "Account Number",
    bank: "Bank Name",
    branch: "Branch Name",
    code: "Bank Code",
    ifsc: "IFSC Code",
    issued: "Cheque Issued Date",
    issuedTo: "Issued To"
  }
};

const EditDocumentOcr = ({ editFieldData, setEditFieldData, fields }) => {
  const handleChangeInput = (name, value) => {
    if (name === "document_type") {
      // Reset output_mapping on document_type change
      setEditFieldData({
        ...editFieldData,
        document_type: value,
        output_mapping: [] // Clear mappings to sync with new type
      });
    } else {
      setEditFieldData({ ...editFieldData, [name]: value });
    }
  };

  const handleChangeValidationInput = (name, value) => {
    setEditFieldData({
      ...editFieldData,
      validate: { ...editFieldData.validate, [name]: value }
    });
  };

  return (
    <div className="builder_form">
      <Input
        label={getLabelForKey("label")}
        value={editFieldData.label}
        name="label"
        onChange={({ target }) => handleChangeInput(target.name, target.value)}
      />
      <Input
        label={getLabelForKey("form_display_label")}
        value={editFieldData.form_display_label}
        name="form_display_label"
        onChange={({ target }) => handleChangeInput(target.name, target.value)}
      />

      <div className="field_horizontal_container">
        <SingleSelectBox
          options={[
            { name: "Default", value: "default" },
            { name: "Drag and Drop", value: "drag_and_drop" }
          ]}
          valueKey="value"
          labelKey={["name"]}
          selectedData={editFieldData?.layout_type || ""}
          onChange={(selected) => handleChangeInput("layout_type", selected?.value || "")}
          label="Select Layout"
          placeholder="Select Layout"
          isSearch
        />
      </div>
      <div className="field_horizontal_container">
        <MultiSelectBox
          options={ocrFormats}
          valueKey="id"
          labelKey={["name"]}
          selectedData={editFieldData.acceptable_formats || []}
          onChange={(selected) => handleChangeInput("acceptable_formats", selected)}
          label="Acceptable Formats"
          placeholder="Select Formats"
          isSearch
        />
      </div>

      <div className="flex_column gap_4 mt_2">
        <div className="field_horizontal_container">
          <Input
            type="checkbox"
            label={getLabelForKey("required")}
            checked={editFieldData.validate.required}
            name="required"
            onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
            containerClassName="w_50"
          />
          <Input
            type="checkbox"
            label={getLabelForKey("disable")}
            checked={editFieldData.disable}
            name="disable"
            onChange={({ target }) => handleChangeInput(target.name, target.checked)}
            containerClassName="w_50"
          />
        </div>

        <div className="field_horizontal_container">
          <Input
            type="checkbox"
            label={getLabelForKey("from_camera")}
            checked={editFieldData.from_camera}
            name="from_camera"
            onChange={({ target }) => handleChangeInput(target.name, target.checked)}
            containerClassName="w_50"
          />
          <Input
            type="checkbox"
            label={getLabelForKey("backend_validation")}
            checked={editFieldData.validate.backend_validation}
            name="backend_validation"
            onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
            containerClassName="w_50"
          />
        </div>

        <div className="field_horizontal_container">
          <Input
            type="checkbox"
            label={getLabelForKey("mandatory")}
            checked={editFieldData.validate.mandatory}
            name="mandatory"
            onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
            containerClassName="w_50"
          />
        </div>
      </div>

      <SingleSelectBox
        label="Document Type"
        options={DOCUMENT_TYPES.map((type) => ({ name: type, id: type }))}
        selectedData={editFieldData.document_type}
        onChange={(val) => handleChangeInput("document_type", val.id)}
      />

      <AiModelSelector
        aiModels={editFieldData.ai_models || []}
        setAiModels={(models) => handleChangeInput("ai_models", models)}
      />

      <DocumentOcrOutputMapping
        mapping={editFieldData.output_mapping || []}
        onChange={(newMapping) => setEditFieldData({ ...editFieldData, output_mapping: newMapping })}
        documentType={editFieldData.document_type}
        formFields={fields}
      />
      <Input
        label={getLabelForKey("help_text")}
        value={editFieldData.help_text}
        name="help_text"
        onChange={({ target }) => handleChangeInput(target.name, target.value)}
      />
    </div>
  );
};

const DocumentOcrOutputMapping = ({ mapping = [], onChange, documentType, formFields = [] }) => {
  let fieldOptions = [];
  let errorMessage = null;

  try {
    if (!documentType) {
      errorMessage = "Please select a document type to set output mappings.";
    } else if (!OUTPUT_LABELS_BY_TYPE[documentType]) {
      errorMessage = `No output mappings available for document type: "${documentType}".`;
    } else {
      fieldOptions = Object.entries(OUTPUT_LABELS_BY_TYPE[documentType] || {}).map(([key, label]) => ({
        id: key,
        name: label
      }));

      if (fieldOptions.length === 0) {
        errorMessage = `No predefined output fields found for "${documentType}".`;
      }
    }

    if (!Array.isArray(formFields)) {
      errorMessage = "Form fields list is invalid.";
    }
  } catch (err) {
    errorMessage = "An unexpected error occurred while loading output mappings.";
  }

  const handleChange = (index, keyOrValue, val) => {
    const updated = [...mapping];
    if (!updated[index]) updated[index] = { key: "", value: "" };
    updated[index][keyOrValue] = val;
    onChange(updated);
  };

  const addMapping = () => {
    onChange([...mapping, { key: "", value: "" }]);
  };

  const removeMapping = (index) => {
    const updated = [...mapping];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div className="mt_2">
      <Label>Output Mapping [Output key to form field]</Label>

      {errorMessage ? (
        <ErrorMessage>{errorMessage}</ErrorMessage>
      ) : (
        <>
          {(mapping || []).map((item, index) => (
            <div key={index} className="field_horizontal_container gap_2 mt_1 align_end">
              <SingleSelectBox
                options={fieldOptions}
                selectedData={item.key}
                onChange={(val) => handleChange(index, "key", val.id)}
                containerClassName="w_50"
              />
              <SingleSelectBox
                labelKey={"label"}
                options={formFields}
                selectedData={item.value}
                onChange={(val) => handleChange(index, "value", val.id)}
                containerClassName="w_50"
              />
              <ButtonActionIcon type={"delete"} onClick={() => removeMapping(index)} />
            </div>
          ))}

          <div className="d_flex justify_end mt_2">
            <Button onClick={addMapping}>+ Add Mapping</Button>
          </div>
        </>
      )}
    </div>
  );
};

export { DocumentOcrOutputMapping };

export default EditDocumentOcr;
